import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FinancasQueue, QueueBrands, QueueOffice, QueueOrigin, getFinancasQueue } from '../../api/financasOffices';
import CaseTenantBadge from '../CaseTenantBadge';
import './FinancasQueuePage.css';

// Cases waiting longer than this many business days at an office are flagged.
export const OVERDUE_BUSINESS_DAYS = 10;

const formatDate = (value: string | null | undefined): string => {
  if (!value) return 'n/a';
  const date = new Date(value);
  if (isNaN(date.getTime())) return 'n/a';
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const businessDays = (value: number | null | undefined): string =>
  value === null || value === undefined ? 'n/a' : `${value} bd`;

// Total waiting across every office. Cases without an office may come back as
// their own block (office_id null) or only as unassigned_waiting; count them
// once either way.
export const totalWaiting = (queue: FinancasQueue): number => {
  const listed = queue.offices.reduce((sum, office) => sum + (office.waiting || 0), 0);
  const listedUnassigned = queue.offices
    .filter(office => !office.office_id)
    .reduce((sum, office) => sum + (office.waiting || 0), 0);
  return listed + Math.max(0, (queue.unassigned_waiting || 0) - listedUnassigned);
};

const OfficeBlock: React.FC<{ office: QueueOffice }> = ({ office }) => {
  const cases = [...(office.cases || [])].sort(
    (a, b) => (b.business_days_waiting || 0) - (a.business_days_waiting || 0)
  );
  const name = office.office_id ? office.office_name || 'Unnamed office' : 'No office recorded';

  return (
    <section className="queue-office" aria-label={name}>
      <div className="queue-office-header">
        <h3 className={office.office_id ? '' : 'queue-office-name--unassigned'}>{name}</h3>
        <span className="queue-office-waiting">{office.waiting} waiting</span>
      </div>
      <dl className="queue-office-stats">
        <div>
          <dt>Oldest waiting</dt>
          <dd>{businessDays(office.oldest_waiting_business_days)}</dd>
        </div>
        <div>
          <dt>Median waiting</dt>
          <dd>{businessDays(office.median_waiting_business_days)}</dd>
        </div>
        <div>
          <dt>Completed (30d)</dt>
          <dd>{office.completed_last_30d ?? 0}</dd>
        </div>
        <div>
          <dt>Median Submitted to Complete (30d)</dt>
          <dd>{businessDays(office.median_submitted_to_complete_business_days_30d)}</dd>
        </div>
      </dl>
      {cases.length > 0 ? (
        <table className="queue-cases">
          <thead>
            <tr>
              <th>Client</th>
              <th>Submitted</th>
              <th>Business days waiting</th>
            </tr>
          </thead>
          <tbody>
            {cases.map(caseItem => {
              const overdue = (caseItem.business_days_waiting ?? 0) > OVERDUE_BUSINESS_DAYS;
              return (
                <tr key={caseItem.case_id} className={overdue ? 'queue-case--overdue' : ''}>
                  <td>
                    <Link to={`/case/${caseItem.case_id}`} className="service-link">
                      {caseItem.client_name || caseItem.case_id}
                    </Link>
                    <CaseTenantBadge tenant={caseItem.tenant ?? undefined} />
                  </td>
                  <td>{formatDate(caseItem.submitted_at)}</td>
                  <td>
                    <span
                      className={`queue-days${overdue ? ' queue-days--overdue' : ''}`}
                      title={overdue ? `Waiting more than ${OVERDUE_BUSINESS_DAYS} business days` : undefined}
                    >
                      {caseItem.business_days_waiting ?? 'n/a'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p className="queue-office-empty">Nothing waiting at this office.</p>
      )}
    </section>
  );
};

/**
 * Which brand's cases the queue shows. Coepi and white-label partner cases sit
 * at the same Finanças offices; a partner's customer has never heard of Coepi,
 * so the lawyer can work one brand at a time. The counts are over the whole
 * queue, so every brand with something waiting stays pickable.
 */
const BrandPicker: React.FC<{
  brands: QueueBrands;
  value: QueueOrigin;
  onChange: (origin: QueueOrigin) => void;
}> = ({ brands, value, onChange }) => {
  const partnersWaiting = brands.partners.reduce((sum, p) => sum + p.waiting, 0);
  return (
    <select
      className="status-select"
      value={value}
      onChange={e => onChange(e.target.value)}
      aria-label="Filter by who the case belongs to"
    >
      <option value="all">All cases ({brands.coepi_waiting + partnersWaiting})</option>
      <option value="coepi">Coepi only ({brands.coepi_waiting})</option>
      <option value="partner">Partners only ({partnersWaiting})</option>
      {brands.partners.map(p => (
        <option key={p.tenantId} value={`tenant:${p.tenantId}`}>
          {p.tenantName} ({p.waiting})
        </option>
      ))}
    </select>
  );
};

const FinancasQueuePage: React.FC = () => {
  const { currentUser } = useAuth();
  const [queue, setQueue] = useState<FinancasQueue | null>(null);
  const [origin, setOrigin] = useState<QueueOrigin>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      setQueue(await getFinancasQueue(token, origin));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the Finanças queue.');
    } finally {
      setLoading(false);
    }
  }, [currentUser, origin]);

  useEffect(() => {
    load();
  }, [load]);

  const total = queue ? totalWaiting(queue) : 0;
  // Offices with nothing waiting still carry turnaround numbers worth seeing,
  // but an office with no activity at all is just noise.
  const offices = (queue?.offices || []).filter(
    office => office.waiting > 0 || (office.completed_last_30d || 0) > 0
  );

  return (
    <div className="financas-queue-page">
      <div className="task-view-header">
        <h2>Finanças queue</h2>
        <div className="queue-header-actions">
          {/* Only once there is a partner case at Finanças, or a brand is picked: until
              then every case is Coepi's and the picker would choose nothing. */}
          {queue?.brands && (queue.brands.partners.length > 0 || origin !== 'all') && (
            <BrandPicker brands={queue.brands} value={origin} onChange={setOrigin} />
          )}
          <button type="button" className="download-button" onClick={load} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>
      {loading && !queue ? (
        <p>Loading queue...</p>
      ) : error ? (
        <div className="status-error-banner" role="alert">{error}</div>
      ) : queue ? (
        <>
          <p className="queue-summary" data-testid="queue-summary">
            <strong>{total}</strong> waiting at Finanças
            {' · '}
            <strong>{queue.unassigned_waiting || 0}</strong> without an office recorded
            {queue.generated_at && (
              <span className="queue-generated"> (as of {formatDateTime(queue.generated_at)})</span>
            )}
          </p>
          {total === 0 && <p className="queue-empty">No cases waiting at Finanças</p>}
          {offices.map(office => (
            <OfficeBlock key={office.office_id || 'unassigned'} office={office} />
          ))}
        </>
      ) : null}
    </div>
  );
};

export default FinancasQueuePage;
